import { TeiaTwinApiClientProvider } from '@stereograph/teia-system-design/teia-components';
import { ApiClient } from '@stereograph/teia-system-design/utils';
import {
  CameraGizmo,
  Command,
  Dock,
  DockProps,
  Environment,
  LoadFileCommand,
  LoadTwinCommand,
  MapPlugin,
  TeiaAuthProvider,
  TeiaViewer,
  TeiaViewport,
  Toolbar,
  ToolbarItemGroup,
  ToolbarItems,
  useTeiaAuth,
} from '@stereograph/teiaviewer';
import {
  ChatBotPlugin,
  OpenDataPlugin,
  PatchDataPlugin,
  QuantityTakeOffPlugin,
  TeiaAnomalyPlugin,
  TeiaSelectionInfoPlugin,
} from '@stereograph/teiaviewer-plugins';
import React from 'react';
import { ShowDevToolsWidgetBhavior } from './behaviors/ShowDevToolsWidgetBehavior';
import { ENV_VARIABLES } from './env';

const viewer = new TeiaViewer({
  debug: true,
  dev: {
    enableAuthentication: true,
  },
});

viewer.addBehavior(new ShowDevToolsWidgetBhavior(viewer));
viewer.registerPlugin('map', new MapPlugin(viewer));
viewer.registerPlugin('qto', new QuantityTakeOffPlugin(viewer));
viewer.registerPlugin('patchData', new PatchDataPlugin(viewer));
viewer.registerPlugin('openData', new OpenDataPlugin(viewer));
viewer.registerPlugin('selectionInfo', new TeiaSelectionInfoPlugin(viewer));
viewer.registerPlugin(
  'anomaly',
  new TeiaAnomalyPlugin(viewer, {
    identityApiUrl: ENV_VARIABLES.VITE_IDENTITY_API_BASE_URL,
  }),
);
viewer.registerPlugin(
  'chatbot',
  new ChatBotPlugin(viewer, {
    tiaApiUrl: ENV_VARIABLES.VITE_ELLIOT_API_BASE_URL,
  }),
);

/**
 * App component setting up a viewer and a viewport
 * @returns
 */
export const App = () => {
  const apiClient = new ApiClient('https://teiatwin-develop.teia-solution.com/');

  return (
    <div className="st-h-screen st-w-screen">
      <TeiaAuthProvider defaultEnvironment={Environment.Develop}>
        <TeiaTwinApiClientProvider client={apiClient}>
          <AutoLoadScene />
          <TeiaViewport viewer={viewer}>
            <CameraGizmo />
            <AppDock
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />
            <AppToolbar />
          </TeiaViewport>
        </TeiaTwinApiClientProvider>
      </TeiaAuthProvider>
    </div>
  );
};

const AutoLoadScene = () => {
  const executeCommandsOnce = React.useRef(false);
  const { isAuthenticated } = useTeiaAuth();

  React.useEffect(() => {
    if (isAuthenticated) {
      if (!executeCommandsOnce.current) {
        executeCommandsOnce.current = true;
        setTimeout(() => {
          autoLoadTwinOrFile();
        }, 300);
      }
    }
  }, [isAuthenticated]);

  /**
   * Automatically loads a Twin or a File if there is projectId={pid}/fileId={fid} in the url.
   * @returns
   */
  const autoLoadTwinOrFile = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('projectId');
    const fileId = urlParams.get('fileId');
    if (projectId !== null) {
      let cmd: Command = new LoadTwinCommand(viewer, Number(projectId), {
        twinType: 'Draft',
      });
      if (fileId !== null) {
        cmd = new LoadFileCommand(viewer, Number(projectId), Number(fileId));
      }
      cmd.execute();
    }
  };

  return <></>;
};

export const AppDock = (props: DockProps) => {
  return (
    <Dock {...props}>
      <ToolbarItemGroup>
        <ToolbarItems.NotificationCenter />
        <ToolbarItems.ShowSettingsWidget />
      </ToolbarItemGroup>
      <ToolbarItemGroup>
        <ToolbarItems.SelectionInfoMenu>
          <ToolbarItems.HideObjects />
          <ToolbarItems.FocusObjects />
        </ToolbarItems.SelectionInfoMenu>
      </ToolbarItemGroup>
      <ToolbarItemGroup>
        <ToolbarItems.ResetScene />
        <ToolbarItems.RenderMode />
        <ToolbarItems.CameraMode />
        <ToolbarItems.Search />
        <ToolbarItems.ShowInfoBox />
        <ToolbarItems.ShowTreeView />
        <ToolbarItems.ScreenShot />
        <ToolbarItems.UndoCommand />
        <ToolbarItems.RedoCommand />
        <ToolbarItems.ShowDevTools />
        <ToolbarItems.ShowDevStats />
        <ToolbarItems.ShowCommandHistory />
        <ToolbarItems.ShowToolbar />
      </ToolbarItemGroup>
    </Dock>
  );
};

export const AppToolbar = () => {
  return (
    <Toolbar
      sx={{
        position: 'absolute',
        top: '50%',
        right: 8,
        transform: 'translateY(-50%)',
      }}
    >
      <ToolbarItemGroup>
        <ToolbarItems.ClippingPlanes />
        <ToolbarItems.ShowProjectionsWidget />
      </ToolbarItemGroup>
      <ToolbarItemGroup>
        <QuantityTakeOffPlugin.ToolbarItems.ShowQTOWidget />
        <PatchDataPlugin.ToolbarItems.ShowTeiaPatchDataWidget />
        <OpenDataPlugin.ToolbarItems.ShowTeiaOpenDataWidget />
        <TeiaAnomalyPlugin.ToolbarItems.ShowTeiaAnomaly />
        <TeiaSelectionInfoPlugin.ToolbarItems.ShowTeiaSelectionRecordWidget />
        <ChatBotPlugin.ToolbarItems.ChatBotToolbarItem />
        <TeiaSelectionInfoPlugin.ToolbarItems.SaveTeiaObjectsSelection />
      </ToolbarItemGroup>
    </Toolbar>
  );
};
